/**
 * The checker's document replica and its CheckContext (W2-12a checker-runtime; ARCHITECTURE.md §3.2, §9;
 * DESIGN.md §5.10).
 *
 * `createCheckReplica(doc, seq)` holds what the analysis worker checks: a DocContainer replica of the main-thread
 * document (the same container code as the DocStore, so the replica stays identical), the sequence number of the
 * last transaction applied, and an Engine with purpose 'check' (the same engine code as the preview, compiling
 * eagerly) fed by the replica's change notifications. Committed transactions are applied strictly in order:
 * `apply(seq, ops)` reports a duplicate ('stale'), a sequence gap ('gap') or ops that do not fit the replica
 * ('failed', the replica is then unusable until `reset`); the session answers the last two with a full resync from
 * a serialized snapshot (`reset`).
 *
 * `createReplicaContext(replica, ext)` is the CheckContext the runner hands to the rules: the replica's document,
 * indexes, tempo map and engine (read through getters, so a reset replica is picked up), the checker defaults for
 * thresholds and environment (the runner overrides them with the configured values), and the optional runtime
 * members of the contract (custom shader sources for PH-07, preview compile errors for PH-33).
 *
 * Also here, shared by the runtime and the style rules:
 *  - `CHECK_ENVIRONMENT_DEFAULTS`: Phira's default player settings and respack metrics, the 1920 x 1080 reference
 *    viewport, unknown song length;
 *  - `PLACEHOLDER_ILLUSTRATION_PATH`: the package path of the gradient placeholder illustration the new-chart
 *    wizard writes when the Picture step is skipped (DESIGN.md §5.1 step 3; style rule ST-02);
 *  - `enabledRuleIds`: the rule ids to run for the user's disabled list.
 */

import { createDocContainer } from '../doc/container';
import { createEngine } from '../engine/engine';
import type { CreateEngine, Engine, EngineOptions, EngineSource } from '../engine/types';
import { VERIFY_PLAYER, VERIFY_SKIN } from '../engine/verify';
import type { AssetId, ChartDoc, DocIndexes } from '../model/types';
import { Ops } from '../ops/ops';
import type { ChangeSet, DirtyScopes, DocContainer, Op } from '../ops/types';
import type { Disposable } from '../util/types';
import { CHECK_THRESHOLD_DEFAULTS, REFERENCE_VIEWPORT } from './geometry';
import type { CheckEnvironment } from './runner';
import type { CheckContext, CheckRule } from './types';

// ---------------------------------------------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------------------------------------------

/**
 * Package path of the generated placeholder illustration (DESIGN.md §5.1 step 3: "a gradient placeholder generated
 * from the title (written as illustration.png)"). A picture the user chose is stored under its own name, so an
 * illustration asset at exactly this path in a chart made in the editor is the placeholder (ST-02).
 */
export const PLACEHOLDER_ILLUSTRATION_PATH = 'illustration.png';

/** Check environment before the app sends its own (Phira defaults, 1920 x 1080, unknown song length). */
export const CHECK_ENVIRONMENT_DEFAULTS: CheckEnvironment = Object.freeze({
  player: VERIFY_PLAYER,
  skin: VERIFY_SKIN,
  refViewport: REFERENCE_VIEWPORT,
  songLengthSec: null,
  songLengthAsset: null,
});

/** Ids of the rules to run: every rule whose id is not in the user's disabled list. */
export function enabledRuleIds(rules: readonly Pick<CheckRule, 'id'>[], disabled: readonly string[]): string[] {
  const off = new Set(disabled);
  return rules.map((r) => r.id).filter((id) => !off.has(id));
}

// ---------------------------------------------------------------------------------------------------------------
// Replica
// ---------------------------------------------------------------------------------------------------------------

/** What happened to a committed transaction offered to the replica. */
export type ReplicaApply =
  /** Applied (seq was lastSeq + 1). */
  | { readonly kind: 'applied'; readonly dirty: DirtyScopes }
  /** Already applied (seq <= lastSeq): ignored. */
  | { readonly kind: 'stale' }
  /** seq > lastSeq + 1: a transaction is missing; nothing was applied. */
  | { readonly kind: 'gap' }
  /** The ops did not fit the replica (it diverged); the replica must be reset. */
  | { readonly kind: 'failed'; readonly error: unknown };

export interface CheckReplica extends Disposable {
  readonly doc: ChartDoc;
  readonly idx: DocIndexes;
  /** Sequence number of the last transaction applied (or of the snapshot). */
  readonly seq: number;
  readonly engine: Engine;
  /** False after a failed apply until the next `reset`. */
  readonly usable: boolean;
  /** Bumps on every applied transaction and every reset. */
  readonly revision: number;
  apply(seq: number, ops: readonly Op[]): ReplicaApply;
  /** Replaces the whole document (snapshot resync); the engine is rebuilt. */
  reset(doc: ChartDoc, seq: number): void;
}

export interface CheckReplicaOptions {
  /** Engine factory (tests may pass a recording one); default the real engine. */
  readonly createEngine?: CreateEngine;
  readonly lower?: EngineOptions['lower'];
}

class ReplicaSource implements EngineSource {
  readonly container: DocContainer;
  private readonly listeners = new Set<(cs: ChangeSet) => void>();
  constructor(container: DocContainer) {
    this.container = container;
  }
  get doc(): ChartDoc {
    return this.container.doc;
  }
  get idx(): DocIndexes {
    return this.container.idx;
  }
  subscribe(cb: (cs: ChangeSet) => void): () => void {
    this.listeners.add(cb);
    return () => {
      this.listeners.delete(cb);
    };
  }
  emit(cs: ChangeSet): void {
    for (const cb of [...this.listeners]) cb(cs);
  }
}

export function createCheckReplica(doc: ChartDoc, seq: number, opts: CheckReplicaOptions = {}): CheckReplica {
  const makeEngine = opts.createEngine ?? createEngine;
  const engineOpts: EngineOptions =
    opts.lower === undefined ? { purpose: 'check' } : { purpose: 'check', lower: opts.lower };
  let source = new ReplicaSource(createDocContainer(doc));
  let engine = makeEngine(source, engineOpts);
  let lastSeq = seq;
  let usable = true;
  let revision = 0;

  return {
    get doc() {
      return source.doc;
    },
    get idx() {
      return source.idx;
    },
    get seq() {
      return lastSeq;
    },
    get engine() {
      return engine;
    },
    get usable() {
      return usable;
    },
    get revision() {
      return revision;
    },
    apply(s, ops) {
      if (!usable) return { kind: 'failed', error: new Error('replica diverged; waiting for a resync') };
      if (s <= lastSeq) return { kind: 'stale' };
      if (s !== lastSeq + 1) return { kind: 'gap' };
      let dirty: DirtyScopes;
      try {
        dirty = Ops.dirtyOf(ops, source.doc);
        source.container.applyAll(ops);
      } catch (error) {
        usable = false;
        return { kind: 'failed', error };
      }
      lastSeq = s;
      revision++;
      source.emit({ source: 'txn', seq: s, ops, dirty });
      return { kind: 'applied', dirty };
    },
    reset(next, s) {
      engine.dispose();
      source = new ReplicaSource(createDocContainer(next));
      engine = makeEngine(source, engineOpts);
      lastSeq = s;
      usable = true;
      revision++;
    },
    dispose() {
      engine.dispose();
    },
  };
}

// ---------------------------------------------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------------------------------------------

/** Runtime members the session keeps up to date (CheckContext.shaderSources / shaderErrors). */
export interface ContextExtensionsSource {
  shaderSources(): ReadonlyMap<AssetId, string>;
  shaderErrors(): ReadonlyMap<AssetId, string>;
}

/** The base CheckContext (without `emit`) over a replica, plus the runtime extensions. */
export function createReplicaContext(
  replica: Pick<CheckReplica, 'doc' | 'idx' | 'engine'>,
  ext: ContextExtensionsSource,
): Omit<CheckContext, 'emit'> {
  return {
    get doc() {
      return replica.doc;
    },
    get idx() {
      return replica.idx;
    },
    get tempo() {
      return replica.engine.tempo;
    },
    get engine() {
      return replica.engine;
    },
    thresholds: CHECK_THRESHOLD_DEFAULTS,
    songLengthSec: CHECK_ENVIRONMENT_DEFAULTS.songLengthSec,
    songLengthAsset: CHECK_ENVIRONMENT_DEFAULTS.songLengthAsset,
    player: CHECK_ENVIRONMENT_DEFAULTS.player,
    skin: CHECK_ENVIRONMENT_DEFAULTS.skin,
    refViewport: CHECK_ENVIRONMENT_DEFAULTS.refViewport,
    get shaderSources() {
      return ext.shaderSources();
    },
    get shaderErrors() {
      return ext.shaderErrors();
    },
  };
}
